// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Squirm — pure game engine. No DOM, no network, no Math.random.

import { makeRng, shuffle } from '@ben-gy/game-engine/rng';

/**
 * The six critters. Each has a distinct silhouette (see critters.ts) so colour is
 * never the only signal — colour-blind players read shape.
 */
export type CritterType = 'spider' | 'roach' | 'fly' | 'grub' | 'rat' | 'bat';
export const ALL_CRITTERS: readonly CritterType[] = [
  'spider',
  'roach',
  'fly',
  'grub',
  'rat',
  'bat',
];

/** A dealt card. Copies of a type are identical; `id` only disambiguates references. */
export interface Card {
  id: number;
  type: CritterType;
}

export interface ModeRules {
  /** Which critters are in this deck. */
  types: readonly CritterType[];
  /**
   * Copies of each type = `copiesBase + copiesPerPlayer * players`. The deck MUST
   * scale with player count: a fixed deck goes thin at higher counts (fewer cards
   * each), hands deplete before any pile reaches the threshold, and the game ends
   * on a "ran out of cards" forfeit instead of the signature "collect N" loss —
   * the balance sim measured 90% forfeits at 4p snap before this was scaled.
   */
  copiesBase: number;
  copiesPerPlayer: number;
  /** Collect this many of ONE critter and you are out. */
  threshold: number;
  /** Peek-and-pass allowed (needs a third player to have a legal target). */
  peek: boolean;
  /**
   * How many times each player may peek-and-pass per round. Peeking is a danger
   * ESCAPE, so an unlimited supply makes "always peek" a dominant dodge (the
   * balance sim proved it); a small budget makes it a resource you spend, and
   * keeps the bluff load-bearing. 0 when `peek` is false.
   */
  peekBudget: number;
  /** Per-decision timer in P2P, ms (solo is untimed). */
  decisionMs: number;
}

export interface SeatSpec {
  id: string;
  name: string;
  bot: boolean;
}

/** A card mid-pass. The true `type` is secret in the public view. */
export interface InFlight {
  cardId: number;
  /** SECRET true type. Stripped from publicView; handed via privateView to seers. */
  type: CritterType;
  /** Who currently holds/declared the card and awaits the receiver's call. */
  from: string;
  /** Who must decide (truth / bluff / peek). */
  to: string;
  /** The DECLARED type — public. Free to be a lie. */
  claim: CritterType;
  /** Everyone who has SEEN the true card, in order (giver + peekers). Public. */
  trail: string[];
  /** The very first giver, for the results trail. */
  origin: string;
  /**
   * The receiver has committed to peeking and been shown the card — they must now
   * pass it on (they can no longer guess). A UI two-step for a human: a bot chooses
   * target+claim atomically because the host already hands it the truth.
   */
  peeking?: string;
}

/** How honest a seat's *revealed* declarations turned out — the learnable tell. */
export interface RevealTally {
  truths: number;
  lies: number;
}

export interface Seat {
  id: string;
  name: string;
  bot: boolean;
  /** SECRET hand. Only the owner (and host) ever holds the identities. */
  hand: Card[];
  /** PUBLIC face-up pile. */
  menagerie: Card[];
  alive: boolean;
  /** Public honesty record derived from reveals of THIS seat's claims. */
  tell: RevealTally;
  /** Peeks remaining this round (public). */
  peeksLeft: number;
}

export type Guess = 'truth' | 'bluff';

export type GameEvent =
  | { t: 'pass'; from: string; to: string; claim: CritterType; trueType: CritterType; cardId: number }
  | { t: 'peek'; peeker: string; to: string; claim: CritterType; trueType: CritterType }
  | {
      t: 'resolve';
      receiver: string;
      guess: Guess;
      claim: CritterType;
      trueType: CritterType;
      from: string;
      taker: string;
      receiverCorrect: boolean;
    }
  | { t: 'take'; taker: string; type: CritterType; count: number }
  | { t: 'eliminate'; id: string; byType: CritterType | null };

export interface LogEntry {
  text: string;
  /** Distinguishes tone for the UI. */
  kind: 'pass' | 'peek' | 'caught' | 'fooled' | 'danger' | 'out' | 'info';
}

export type Phase = 'play' | 'over';

/** The most recent reveal, held so the table can animate the flip + stamp. */
export interface Resolve {
  receiver: string;
  from: string;
  taker: string;
  claim: CritterType;
  trueType: CritterType;
  receiverCorrect: boolean;
  cardId: number;
}

export interface GameState {
  seed: number;
  rules: ModeRules;
  seats: Seat[];
  /** Whose turn it is to GIVE, when `inFlight` is null. */
  turn: string;
  inFlight: InFlight | null;
  phase: Phase;
  /** Who triggered the end (collected the threshold, or could not play). */
  loserId: string | null;
  /** Final ranking, BEST first. Filled at game over. */
  ranking: string[];
  log: LogEntry[];
  events: GameEvent[];
  /** Number of resolutions so far — a ply clock for the drama curve. */
  ply: number;
  /** The last reveal, for the UI. Cleared when a new card is passed. */
  lastResolve: Resolve | null;
}

// ---------------------------------------------------------------------------
// Setup
// ---------------------------------------------------------------------------

/** Copies of each critter for a given player count. */
export function deckCopies(rules: ModeRules, players: number): number {
  return rules.copiesBase + rules.copiesPerPlayer * players;
}

function buildDeck(rules: ModeRules, players: number): Card[] {
  const deck: Card[] = [];
  const copies = deckCopies(rules, players);
  let id = 0;
  for (const type of rules.types) {
    for (let c = 0; c < copies; c++) deck.push({ id: id++, type });
  }
  return deck;
}

/**
 * Deal the whole deck evenly into hands. Any remainder is set aside, out of play
 * (as in the physical game). Deterministic from the seed so peers agree.
 */
export function deal(seed: number, specs: readonly SeatSpec[], rules: ModeRules): GameState {
  const rng = makeRng(`${seed}:deal`);
  const deck = shuffle(rng, buildDeck(rules, specs.length));
  const n = specs.length;
  const per = Math.floor(deck.length / n);

  const seats: Seat[] = specs.map((s, i) => ({
    id: s.id,
    name: s.name,
    bot: s.bot,
    hand: deck.slice(i * per, i * per + per),
    menagerie: [],
    alive: true,
    tell: { truths: 0, lies: 0 },
    peeksLeft: rules.peek ? rules.peekBudget : 0,
  }));

  // Randomise who gives first (from the seed, so peers agree). Otherwise seat 0
  // always opens, which the balance sim shows biases the outcome by seat order.
  const firstGiver = specs[Math.floor(makeRng(`${seed}:first`)() * specs.length)].id;

  return {
    seed,
    rules,
    seats,
    turn: firstGiver,
    inFlight: null,
    phase: 'play',
    loserId: null,
    ranking: [],
    log: [{ text: `The table is dealt — ${n} players, ${per} cards each.`, kind: 'info' }],
    events: [],
    ply: 0,
    lastResolve: null,
  };
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export function seatOf(s: GameState, id: string): Seat | undefined {
  return s.seats.find((x) => x.id === id);
}

export function aliveSeats(s: GameState): Seat[] {
  return s.seats.filter((x) => x.alive);
}

/** How many of `type` a seat is holding face-up. */
export function countOf(seat: Seat, type: CritterType): number {
  return seat.menagerie.reduce((n, c) => n + (c.type === type ? 1 : 0), 0);
}

/** The seat's largest single-type pile — its danger level. */
export function maxPile(seat: Seat): { type: CritterType | null; count: number } {
  let best: CritterType | null = null;
  let count = 0;
  for (const type of seat.menagerie.map((c) => c.type)) {
    const c = countOf(seat, type);
    if (c > count) {
      count = c;
      best = type;
    }
  }
  return { type: best, count };
}

/** Alive opponents a giver may pass to. */
export function legalTargets(s: GameState, giverId: string): string[] {
  return aliveSeats(s)
    .filter((x) => x.id !== giverId)
    .map((x) => x.id);
}

/** Alive players the current receiver may peek-and-pass to (outside the seen trail). */
export function peekTargets(s: GameState): string[] {
  if (!s.inFlight || !s.rules.peek) return [];
  const receiver = seatOf(s, s.inFlight.to);
  if (!receiver || receiver.peeksLeft <= 0) return []; // out of peeks — must resolve
  const seen = new Set(s.inFlight.trail);
  return aliveSeats(s)
    .filter((x) => x.id !== s.inFlight!.to && !seen.has(x.id))
    .map((x) => x.id);
}

export function canPeek(s: GameState): boolean {
  return peekTargets(s).length > 0;
}

// ---------------------------------------------------------------------------
// Moves (all pure — return a NEW state, or the same state if illegal)
// ---------------------------------------------------------------------------

function clone(s: GameState): GameState {
  return {
    ...s,
    seats: s.seats.map((x) => ({
      ...x,
      hand: [...x.hand],
      menagerie: [...x.menagerie],
      tell: { ...x.tell },
    })),
    log: [...s.log],
    events: [...s.events],
    ranking: [...s.ranking],
    inFlight: s.inFlight ? { ...s.inFlight, trail: [...s.inFlight.trail] } : null,
  };
}

const NAME = (s: GameState, id: string): string => seatOf(s, id)?.name ?? id;
const CRITTER_LABEL: Record<CritterType, string> = {
  spider: 'spider',
  roach: 'roach',
  fly: 'fly',
  grub: 'grub',
  rat: 'rat',
  bat: 'bat',
};

/** The active giver slides a card face-down and declares a critter. */
export function play(
  s: GameState,
  giverId: string,
  cardId: number,
  toId: string,
  claim: CritterType,
): GameState {
  if (s.phase !== 'play' || s.inFlight) return s;
  if (s.turn !== giverId) return s;
  const giver = seatOf(s, giverId);
  if (!giver || !giver.alive) return s;
  if (!legalTargets(s, giverId).includes(toId)) return s;
  if (!s.rules.types.includes(claim)) return s;
  const idx = giver.hand.findIndex((c) => c.id === cardId);
  if (idx < 0) return s;

  const n = clone(s);
  n.lastResolve = null; // a fresh card in flight — clear the previous reveal
  const g = seatOf(n, giverId)!;
  const [card] = g.hand.splice(idx, 1);
  n.inFlight = {
    cardId: card.id,
    type: card.type,
    from: giverId,
    to: toId,
    claim,
    trail: [giverId],
    origin: giverId,
  };
  n.events.push({ t: 'pass', from: giverId, to: toId, claim, trueType: card.type, cardId: card.id });
  n.log.push({
    text: `${NAME(n, giverId)} slides a card to ${NAME(n, toId)}: "a ${CRITTER_LABEL[claim]}."`,
    kind: 'pass',
  });
  return n;
}

/**
 * The receiver commits to peeking: they are shown the card (privateView reveals it)
 * and can no longer guess. A UI helper for the human flow; bots skip straight to
 * peekPass. No budget is spent until the pass completes.
 */
export function beginPeek(s: GameState, receiverId: string): GameState {
  if (s.phase !== 'play' || !s.inFlight) return s;
  if (s.inFlight.to !== receiverId || s.inFlight.peeking) return s;
  if (!s.rules.peek) return s;
  const receiver = seatOf(s, receiverId);
  if (!receiver || receiver.peeksLeft <= 0) return s;
  if (peekTargets(s).length === 0) return s;
  const n = clone(s);
  n.inFlight!.peeking = receiverId;
  return n;
}

/** The receiver peeks at the true card and passes it on with a fresh claim. */
export function peekPass(
  s: GameState,
  receiverId: string,
  toId: string,
  claim: CritterType,
): GameState {
  if (s.phase !== 'play' || !s.inFlight) return s;
  if (s.inFlight.to !== receiverId) return s;
  if (s.inFlight.peeking && s.inFlight.peeking !== receiverId) return s;
  if (!s.rules.peek) return s;
  const receiver = seatOf(s, receiverId);
  if (!receiver || receiver.peeksLeft <= 0) return s;
  if (!peekTargets(s).includes(toId)) return s;
  if (!s.rules.types.includes(claim)) return s;

  const n = clone(s);
  const f = n.inFlight!;
  seatOf(n, receiverId)!.peeksLeft--;
  f.peeking = undefined;
  n.events.push({ t: 'peek', peeker: receiverId, to: toId, claim, trueType: f.type });
  n.log.push({
    text: `${NAME(n, receiverId)} peeks… and passes it to ${NAME(n, toId)}: "a ${CRITTER_LABEL[claim]}."`,
    kind: 'peek',
  });
  f.trail.push(receiverId);
  f.from = receiverId;
  f.to = toId;
  f.claim = claim;
  return n;
}

/** The receiver calls it: truth (believe) or bluff (doubt). Reveals + resolves. */
export function guess(s: GameState, receiverId: string, g: Guess): GameState {
  if (s.phase !== 'play' || !s.inFlight) return s;
  if (s.inFlight.to !== receiverId) return s;
  if (s.inFlight.peeking) return s; // committed to peeking — must pass, not guess

  const n = clone(s);
  const f = n.inFlight!;
  const reveal = f.type;
  const claimIsTrue = reveal === f.claim;
  const receiverCorrect = g === 'truth' ? claimIsTrue : !claimIsTrue;
  const takerId = receiverCorrect ? f.from : receiverId;

  // Record the claimer's honesty tell (public, from the reveal).
  const claimer = seatOf(n, f.from)!;
  if (claimIsTrue) claimer.tell.truths++;
  else claimer.tell.lies++;

  n.events.push({
    t: 'resolve',
    receiver: receiverId,
    guess: g,
    claim: f.claim,
    trueType: reveal,
    from: f.from,
    taker: takerId,
    receiverCorrect,
  });
  n.log.push({
    text: receiverCorrect
      ? `${NAME(n, receiverId)} was right — it was a ${CRITTER_LABEL[reveal]}. ${NAME(n, f.from)} keeps it.`
      : `${NAME(n, receiverId)} got it wrong — it was a ${CRITTER_LABEL[reveal]}. They keep it.`,
    kind: receiverCorrect ? 'caught' : 'fooled',
  });

  n.inFlight = null;
  n.lastResolve = {
    receiver: receiverId,
    from: f.from,
    taker: takerId,
    claim: f.claim,
    trueType: reveal,
    receiverCorrect,
    cardId: f.cardId,
  };
  n.ply++;
  takeCard(n, takerId, { id: f.cardId, type: reveal });
  if (n.phase === 'play') n.turn = takerId; // whoever took it gives next
  return n;
}

/** Put a revealed card into a seat's menagerie and check for the loss. Mutates n. */
function takeCard(n: GameState, takerId: string, card: Card): void {
  const taker = seatOf(n, takerId)!;
  taker.menagerie.push(card);
  const count = countOf(taker, card.type);
  n.events.push({ t: 'take', taker: takerId, type: card.type, count });
  if (count >= n.rules.threshold) {
    endGame(n, takerId, card.type);
  } else if (count === n.rules.threshold - 1) {
    n.log.push({
      text: `${NAME(n, takerId)} now holds ${count} ${CRITTER_LABEL[card.type]}s — one from out.`,
      kind: 'danger',
    });
  }
}

/**
 * A giver who cannot move (empty hand) is out. Because every resolution moves one
 * card from a hand to a menagerie, hands strictly shrink and this — or a threshold
 * pile — always eventually fires, so the game is guaranteed to terminate.
 */
export function outOfMoves(s: GameState, giverId: string): boolean {
  const g = seatOf(s, giverId);
  if (!g) return false;
  return g.hand.length === 0;
}

/** Resolve a giver who is stuck with no card to play. */
export function forfeit(s: GameState, giverId: string): GameState {
  if (s.phase !== 'play' || s.inFlight) return s;
  if (s.turn !== giverId || !outOfMoves(s, giverId)) return s;
  const n = clone(s);
  n.log.push({ text: `${NAME(n, giverId)} has no card left to pass.`, kind: 'out' });
  endGame(n, giverId, null);
  return n;
}

function endGame(n: GameState, loserId: string, byType: CritterType | null): void {
  const loser = seatOf(n, loserId)!;
  loser.alive = false;
  n.phase = 'over';
  n.loserId = loserId;
  n.events.push({ t: 'eliminate', id: loserId, byType });
  n.inFlight = null;
  n.log.push({
    text: byType
      ? `${loser.name} collected ${n.rules.threshold} ${CRITTER_LABEL[byType]}s — out! Everyone else is safe.`
      : `${loser.name} is out — no card to play.`,
    kind: 'out',
  });
  n.ranking = rankSeats(n, loserId);
}

/**
 * Rank BEST first: the loser is always last; the rest by lowest danger (smallest
 * single-type pile), then fewest total cards, then seat order. No ties in the
 * standings, so there is always a clear winner to celebrate.
 */
export function rankSeats(s: GameState, loserId: string): string[] {
  // Break exact ties with a SEED-DERIVED key, never seat index — otherwise every
  // equally-safe tie hands the win to the lowest seat (the ballast tie-break bug),
  // which the balance sim reads as a fat seat-0 advantage. This key is identical on
  // every peer (pure function of seed + id) so P2P standings agree.
  const tieKey = (id: string): number => makeRng(`${s.seed}:tie:${id}`)();
  const survivors = s.seats.filter((x) => x.id !== loserId);
  survivors.sort((a, b) => {
    const da = maxPile(a).count;
    const db = maxPile(b).count;
    if (da !== db) return da - db;
    if (a.menagerie.length !== b.menagerie.length) return a.menagerie.length - b.menagerie.length;
    return tieKey(a.id) - tieKey(b.id);
  });
  return [...survivors.map((x) => x.id), loserId];
}

// ---------------------------------------------------------------------------
// Views — the public/private split that makes P2P hidden-info safe
// ---------------------------------------------------------------------------

export interface PublicSeat {
  id: string;
  name: string;
  bot: boolean;
  alive: boolean;
  handCount: number;
  menagerie: Card[];
  tell: RevealTally;
  maxCount: number;
  peeksLeft: number;
}

export interface PublicInFlight {
  from: string;
  to: string;
  claim: CritterType;
  trail: string[];
  cardId: number;
  origin: string;
  peeking?: string;
}

export interface PublicState {
  seed: number;
  rules: ModeRules;
  seats: PublicSeat[];
  turn: string;
  inFlight: PublicInFlight | null;
  phase: Phase;
  loserId: string | null;
  ranking: string[];
  log: LogEntry[];
  ply: number;
  lastResolve: Resolve | null;
}

export function publicView(s: GameState): PublicState {
  return {
    seed: s.seed,
    rules: s.rules,
    seats: s.seats.map((x) => ({
      id: x.id,
      name: x.name,
      bot: x.bot,
      alive: x.alive,
      handCount: x.hand.length,
      menagerie: x.menagerie.map((c) => ({ ...c })),
      tell: { ...x.tell },
      maxCount: maxPile(x).count,
      peeksLeft: x.peeksLeft,
    })),
    turn: s.turn,
    inFlight: s.inFlight
      ? {
          from: s.inFlight.from,
          to: s.inFlight.to,
          claim: s.inFlight.claim,
          trail: [...s.inFlight.trail],
          cardId: s.inFlight.cardId,
          origin: s.inFlight.origin,
          peeking: s.inFlight.peeking,
        }
      : null,
    phase: s.phase,
    loserId: s.loserId,
    ranking: [...s.ranking],
    log: s.log.slice(-40),
    ply: s.ply,
    lastResolve: s.lastResolve,
  };
}

export interface PrivateView {
  pub: PublicState;
  /** This player's own hand. */
  hand: Card[];
  /** The in-flight card's true type IF this player has seen it (is in the trail). */
  inFlightTrueType: CritterType | null;
}

export function privateView(s: GameState, id: string): PrivateView | null {
  const seat = seatOf(s, id);
  if (!seat) return null;
  const canSee = s.inFlight && (s.inFlight.trail.includes(id) || s.inFlight.peeking === id);
  const seen = canSee ? s.inFlight!.type : null;
  return {
    pub: publicView(s),
    hand: seat.hand.map((c) => ({ ...c })),
    inFlightTrueType: seen,
  };
}
